import React, { useState, useEffect, useMemo } from "react";
import {
	Box,
	Typography,
	Button,
	Autocomplete,
	TextField,
	Alert,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import ModalWrapper from "../ModalWrapper/ModalWrapper";

const DeleteCategoryModal = ({
	open,
	category,
	categories = [],
	exclusiveDocuments = [],
	categoryDocuments = [],
	defaultCategory,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();
	const [reassignCategoryId, setReassignCategoryId] = useState(null);
	const [error, setError] = useState("");
	const [serverError, setServerError] = useState("");

	useEffect(() => {
		if (open) {
			setReassignCategoryId(null);
			setError("");
			setServerError("");
		}
	}, [open, category]);

	const reassignOptions = useMemo(() => {
		const filtered = categories.filter((cat) => cat.id && cat.id !== category?.id);
		if (
			defaultCategory &&
			defaultCategory.id !== category?.id &&
			!filtered.some((cat) => cat.id === defaultCategory.id)
		) {
			return [...filtered, defaultCategory];
		}
		return filtered;
	}, [categories, category, defaultCategory]);

	const handleConfirm = async () => {
		if (exclusiveDocuments.length > 0 && !reassignCategoryId) {
			setError(
				t("ALERT_MESSAGES.CATEGORY_DELETE_REASSIGN_REQUIRED") ||
					t('UI_TEXT.PLEASE_REASSIGN_DOCUMENTS_OR_CHOOSE_A_FALLBACK_OPTION')
			);
			return;
		}
		setError("");
		try {
			await onConfirm?.({
				reassignCategoryId,
				fallbackToDefault: false,
			});
			setServerError("");
		} catch (err) {
			const message =
				err?.message ||
				err?.response?.data?.message ||
				t("ALERT_MESSAGES.ERROR") ||
				"Something went wrong";
			setServerError(message);
		}
	};

	const renderExclusiveDocuments = () => {
		if (!categoryDocuments.length) {
			return (
				<Alert severity="success">
					{t("UI_TEXT.NO_CATEGORY_DOCUMENTS")}
				</Alert>
			);
		}
		if (!exclusiveDocuments.length) {
			return (
				<Alert severity="info">
					{t("UI_TEXT.CATEGORY_DOCUMENTS_SHARED") ||
						t('UI_TEXT.ALL_DOCUMENTS_IN_THIS_CATEGORY_ARE_ALSO_ASSIGNED_ELSEWHERE_NO_REASSIGNMENT_IS_REQUIRED')}
				</Alert>
			);
		}

		return (
			<>
				<Typography variant="body2" color="text.secondary">
					{t("UI_TEXT.REASSIGN_CATEGORY_INSTRUCTIONS")}
				</Typography>
				<Box>
					<Typography variant="subtitle2" sx={{ mb: 1 }}>
						{t("UI_TEXT.DOCUMENTS_NEEDING_ATTENTION")}
					</Typography>
					<Box
						sx={{
							border: "1px solid #eee",
							borderRadius: 1,
							maxHeight: 180,
							overflowY: "auto",
							p: 1,
						}}
					>
						{exclusiveDocuments.map((doc) => (
							<Typography variant="body2" key={doc.id}>
								• {doc.name || t("TABLE_HEADERS.DOCUMENT_NAME")}
							</Typography>
						))}
					</Box>
				</Box>
				{reassignOptions.length > 0 && (
					<Autocomplete
						size="small"
						options={reassignOptions}
						value={
							reassignOptions.find(
								(cat) => cat.id === reassignCategoryId
							) || null
						}
						getOptionLabel={(option) => option.category_name || ""}
						onChange={(_event, value) => {
							setReassignCategoryId(value?.id || null);
							setError("");
						}}
						renderInput={(params) => (
							<TextField
								{...params}
								label={t("LABELS.REASSIGN_TO_CATEGORY")}
								size="small"
								error={!!error}
								helperText={error}
							/>
						)}
					/>
				)}
			</>
		);
	};

	return (
		<ModalWrapper
			open={open}
			setOpen={() => {}}
			onClose={onClose}
			title={t("BUTTON.DELETE_CATEGORY")}
			subtitle={category?.category_name || ""}
			width="520px"
			content={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 1 }}>
					<Typography variant="body2">
						{t("UI_TEXT.DELETE_CATEGORY_WARNING", {
							category: category?.category_name || "",
						})}
					</Typography>
					{renderExclusiveDocuments()}
					{serverError && <Alert severity="error">{serverError}</Alert>}
				</Box>
			}
			actions={
				<>
					<Button onClick={onClose}>{t("BUTTON.CANCEL")}</Button>
					<Button variant="contained" color="error" onClick={handleConfirm}>
						{t("BUTTON.DELETE")}
					</Button>
				</>
			}
		/>
	);
};

export default DeleteCategoryModal;
